import { Link } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import { getRoutesByCategory } from "@/config/routes";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { ExternalLink } from "lucide-react";

const Sitemap = () => {
  const { language, setLanguage, content } = useLanguage();
  const categories = getRoutesByCategory();

  const getCategoryTitle = (category: string) => {
    const titles = {
      services: language === "sv" ? "Tjänster" : "Services",
      pricing: language === "sv" ? "Priser & Abonnemang" : "Pricing & Subscriptions", 
      company: language === "sv" ? "Företag" : "Company",
      legal: language === "sv" ? "Rättsligt" : "Legal",
      other: language === "sv" ? "Startsida" : "Home"
    };
    return titles[category as keyof typeof titles] || category;
  };

  const getRouteTitle = (route: any) => {
    return language === "sv" ? route.titleSv : route.titleEn;
  };

  const getRouteDescription = (route: any) => {
    return language === "sv" ? route.descriptionSv : route.descriptionEn;
  };

  return (
    <div className="min-h-screen bg-background">
      <Header
        language={language}
        onLanguageChange={setLanguage}
        content={content.header}
      />
      
      <main className="container mx-auto px-4 py-16">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold mb-4">
              {language === "sv" ? "Webbkarta" : "Sitemap"}
            </h1>
            <p className="text-lg text-muted-foreground">
              {language === "sv" 
                ? "Hitta alla sidor på vår webbplats" 
                : "Find all pages on our website"}
            </p>
          </div>

          <div className="grid gap-8 md:gap-12">
            {Object.entries(categories).map(([categoryKey, categoryRoutes]) => {
              if (categoryRoutes.length === 0) return null;
              
              return (
                <div key={categoryKey} className="space-y-4">
                  {categoryKey === 'other' && categoryRoutes.length === 1 && categoryRoutes[0].path === '/' ? (
                    <div className="border rounded-lg p-6 hover:bg-accent/50 transition-colors">
                      <Link to={categoryRoutes[0].path} className="group">
                        <h2 className="text-2xl font-semibold mb-2 group-hover:text-primary transition-colors">
                          {getRouteTitle(categoryRoutes[0])}
                        </h2>
                        <p className="text-muted-foreground">
                          {getRouteDescription(categoryRoutes[0])}
                        </p>
                      </Link>
                    </div>
                  ) : (
                    <>
                      <h2 className="text-2xl font-semibold text-primary border-b pb-2">
                        {getCategoryTitle(categoryKey)}
                      </h2>
                      <div className="grid gap-4 md:grid-cols-2">
                        {categoryRoutes.map((route, routeIndex) => (
                          <div key={routeIndex} className="border rounded-lg p-4 hover:bg-accent/50 transition-colors">
                            <Link to={route.path} className="group block">
                              <h3 className="text-lg font-medium mb-2 group-hover:text-primary transition-colors">
                                {getRouteTitle(route)}
                              </h3>
                              <p className="text-sm text-muted-foreground">
                                {getRouteDescription(route)}
                              </p>
                            </Link>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>

          <div className="mt-12 p-6 bg-muted rounded-lg">
            <h3 className="text-lg font-semibold mb-2 flex items-center gap-2">
              <ExternalLink className="w-5 h-5" />
              {language === "sv" ? "För sökmotorer" : "For Search Engines"}
            </h3>
            <p className="text-muted-foreground mb-3">
              {language === "sv" 
                ? "XML-sitemap för sökmotorer och crawlers"
                : "XML sitemap for search engines and crawlers"}
            </p>
            <a 
              href="/sitemap.xml" 
              className="text-primary hover:underline"
              target="_blank"
              rel="noopener noreferrer"
            >
              sitemap.xml
            </a>
          </div>
        </div>
      </main>

      <Footer content={content.footer} />
    </div>
  );
};

export default Sitemap;